// app/frontend/components/layouts/AuthenticatedLayout.tsx
import { ReactNode } from "react";
import { Box, Button, Divider, Stack } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconVideo, IconBook, IconLayoutDashboard, IconUsers, IconNews } from "@tabler/icons-react";
import { Link, usePage } from "@inertiajs/react";
import { Navbar } from "../ui/Navigation";
import { NavigationDrawer } from "../ui/NavigationDrawer";
import { MenuNavItem } from "../ui/MenuNavItem";

interface AuthLayoutProps {
  children: ReactNode;
  hideOnScroll?: boolean;
}

/**
 * AuthenticatedLayout - Layout for authenticated users
 * Features a gradient background with header and footer
 * Content width is controlled by child components (e.g., PageContainer)
 */
export default function AuthenticatedLayout({
  children,
}: AuthLayoutProps) {
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);
  const { current_user } = usePage<{ current_user: unknown | null }>().props;

  return (
    <Box
      style={{
        minHeight: "100vh",
        background: `
          radial-gradient(ellipse at 20% 50%, rgba(255, 152, 0, 0.15) 0%, transparent 50%),
          radial-gradient(ellipse at 80% 20%, rgba(255, 193, 7, 0.12) 0%, transparent 50%),
          radial-gradient(ellipse at 40% 80%, rgba(255, 87, 34, 0.1) 0%, transparent 50%),
          linear-gradient(135deg, #171717 0%, #1a1a1a 25%, #171717 50%, #1f1f1f 75%, #171717 100%)
        `,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        h={60}
        style={{
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          background: "rgba(0, 0, 0, 0.3)",
          backdropFilter: "blur(10px)",
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <Navbar drawerOpened={drawerOpened} toggleDrawer={toggleDrawer} />
      </Box>

      {/* Mobile Navigation Drawer */}
      <NavigationDrawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title="Menu"
      >
        <Stack gap={8} pt="md">
          <MenuNavItem
            href="#"
            icon={IconLayoutDashboard}
            label="Dashboard · Coming soon"
            disabled
          />
          <MenuNavItem
            href="/videos"
            icon={IconVideo}
            label="Videos"
            onClick={closeDrawer}
          />
          <MenuNavItem
            href="/posts"
            icon={IconNews}
            label="Posts"
            onClick={closeDrawer}
          />
          <MenuNavItem
            href="/dictionary"
            icon={IconBook}
            label="Dictionary"
            onClick={closeDrawer}
          />
          <MenuNavItem
            href="#"
            icon={IconUsers}
            label="Community · Coming soon"
            disabled
          />

          {!current_user && (
            <>
              <Divider color="rgba(255,255,255,0.1)" my="sm" />
              <Button
                component={Link}
                href="/login"
                variant="light"
                c="#FF9800"
                fullWidth
                styles={{ root: { fontWeight: 600 } }}
                onClick={closeDrawer}
              >
                Login
              </Button>
              <Button
                disabled
                variant="filled"
                fullWidth
                title="Sign up opens at 2.0 launch"
                styles={{
                  root: {
                    background: "rgba(255, 152, 0, 0.15)",
                    border: "1px solid rgba(255, 152, 0, 0.25)",
                    color: "rgba(255, 152, 0, 0.6)",
                    fontWeight: 600,
                    cursor: "not-allowed",
                  },
                }}
              >
                Sign Up · Coming soon
              </Button>
            </>
          )}
        </Stack>
      </NavigationDrawer>

      {/* Content - no width constraint, let children control width.
          Small vertical padding on mobile (10px) and generous on desktop
          (20px). Individual pages add their own Container padding on top. */}
      <Box style={{ flex: 1 }} py={{ base: "xs", md: "lg" }}>
        {children}
      </Box>

    </Box>
  );
}
